import { JSX, useState, useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import { Heading } from "@otrium/atoms";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import { t, Trans } from "@lingui/macro";
import Link from "next/link";
import layout from "src/styles/layout.module.scss";
import { ExploreMoreCarousel } from "src/molecules/ExploreMoreCarousel";
import { HomeBrandCover } from "src/molecules/HomeBrandCover";
import type {
  HomepageDepartmentSection,
  HomepageShopSection,
  ShopType,
} from "src/types/graphql.d";
import { HyperLink } from "src/atoms/HyperLink";
import getAllBrandsPageRoute from "src/utils/getAllBrandsPageRoute";
import { locale } from "src/config/app-config";
import { useLingui } from "@lingui/react";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { LoginRegisterModal } from "src/molecules/LoginRegisterModal";
import {
  deleteSessionStorageItem,
  setSessionStorageJSON,
} from "src/utils/helper/sessionStorage";
import { GATED_POPUP_REDIRECT_DATA } from "src/constants/gatedItem";
import {
  SEGMENT_LIST_FORMAT,
  SEGMENT_PROMOTION_TYPE,
  SEGMENT_LIST_TYPE,
} from "src/segment";
import { ChevronTextSmall } from "src/icons/ChevronTextSmall";
import { SUPPORTED_KEYS } from "src/utils/constants";
import type { CarouselData, SupportedKeys } from "src/types/Carousels";
import { trackCarouselViewAllSelectedEvent } from "src/tracking/events/carousel-view-all-selected";
import { useIsClient } from "src/hooks/use-is-client";

type BrandsSectionProps = {
  shops: HomepageShopSection[];
  department?: HomepageDepartmentSection;
  genderId: string;
  order?: number;
  shopType: string;
};

function BrandsSection({
  shops,
  department,
  genderId,
  order,
  shopType,
}: BrandsSectionProps): JSX.Element {
  const { i18n } = useLingui();
  const { asPath: path } = useRouter();
  const { enableGatedHomepage } = useFeatureFlags();
  const { isMobile } = useBreakpoints();
  const [activeSection, setActiveSection] = useState<SupportedKeys>(
    SUPPORTED_KEYS[1]
  );
  const isClient = useIsClient();

  const isLoggedIn = useIsLoggedIn();
  const [open, setOpen] = useState<boolean>(false);
  const [actionSource, setActionSource] = useState<string>("");
  const featuredBrand = shops?.find(({ type }) => type === "featured_shops")
    ?.data?.[0];
  const allBrandsLink = getAllBrandsPageRoute({
    shopType: genderId as ShopType,
    locale,
  });

  const isGated: boolean = useMemo(
    () =>
      typeof enableGatedHomepage === "boolean" &&
      enableGatedHomepage &&
      !isLoggedIn &&
      (featuredBrand?.gate?.level === "fully_gated" ||
        featuredBrand?.gate?.level === "partially_gated"),
    [enableGatedHomepage, isLoggedIn, featuredBrand?.gate?.level]
  );

  const carouselData: CarouselData[] = useMemo(() => {
    const newData: CarouselData[] = [];

    const availableShops = shops
      .filter(
        (shop) =>
          shop.data.length >= 3 &&
          SUPPORTED_KEYS.includes(shop.type as SupportedKeys)
      )
      .map((shop) => ({
        title: shop.title ?? "",
        data: shop.data,
        type: shop.type as SupportedKeys,
      }));

    newData.push(...availableShops);

    if (
      department &&
      department.data.brands &&
      department.data.brands?.length >= 3
    ) {
      newData.push({
        title: department.data.department_title,
        data: department.data.brands,
        type: department.type as SupportedKeys,
      });
    }

    newData.sort(
      (a, b) => SUPPORTED_KEYS.indexOf(a.type) - SUPPORTED_KEYS.indexOf(b.type)
    );

    return newData;
  }, [shops, department]);

  const handleViewAllClick = useCallback(() => {
    if (isGated) {
      setOpen(true);
      setActionSource("feature_brand_view_all");

      return;
    }

    void trackCarouselViewAllSelectedEvent({
      carouselId: activeSection,
      carouselPosition: 1,
      contentType: "brand",
      shopType,
      items: [],
      title: isGated ? t(i18n)`Unlock the best fashion deals` : t(i18n)`Brands`,
    });
  }, [isGated, shopType, activeSection, i18n]);

  const setRedirectData = (type: string) => {
    deleteSessionStorageItem(GATED_POPUP_REDIRECT_DATA);
    setSessionStorageJSON(GATED_POPUP_REDIRECT_DATA, {
      gatedActionType: type,
      promo_id: featuredBrand?.id,
      promo_name: featuredBrand?.name || featuredBrand?.subtitle,
      promo_creative: featuredBrand?.image,
      promo_position: "homepage-featured-shop-1",
      promo_type: SEGMENT_PROMOTION_TYPE.featuredShop,
      promo_path: path,
      productListMeta: {
        listFormat: SEGMENT_LIST_FORMAT.carousel,
        listId: featuredBrand?.slug,
        listType: SEGMENT_LIST_TYPE.brand,
        shopType: genderId,
      },
    });
  };

  return (
    <Box
      pt={["space32", "space32", "space64", "space64"]}
      pb={["space16", "space16", "space32", "space32"]}
      as="section"
      order={order}
      className={layout.containerGrid}
    >
      {featuredBrand && (
        <>
          <div>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              pb={3}
            >
              {!isGated ? (
                <Link href={allBrandsLink} onClick={handleViewAllClick}>
                  <Heading
                    as="h3"
                    fontFamily="PlusJakartaSans"
                    fontSize={[2, 2, 3, 3]}
                    color="Black"
                    sx={{
                      lineHeight: 1.25,
                      textTransform: "none",
                    }}
                  >
                    {isGated
                      ? t(i18n)`Unlock the best fashion deals`
                      : t(i18n)`Brands`}
                    {isClient && isMobile ? (
                      <ChevronTextSmall role="presentation" />
                    ) : null}
                  </Heading>
                </Link>
              ) : (
                <Heading
                  as="h3"
                  fontFamily="PlusJakartaSans"
                  fontSize={[2, 2, 3, 3]}
                  color="Black"
                  sx={{
                    lineHeight: 1.25,
                    textTransform: "none",
                  }}
                  onClick={handleViewAllClick}
                >
                  {isGated
                    ? t(i18n)`Unlock the best fashion deals`
                    : t(i18n)`Brands`}
                  {isClient && isMobile ? (
                    <ChevronTextSmall role="presentation" />
                  ) : null}
                </Heading>
              )}

              {isClient && !isMobile ? (
                <Box pb={[3, 3, 3, 3, 0]} sx={{ zIndex: 2 }}>
                  {isGated ? (
                    <HyperLink hasUnderline asLink onClick={handleViewAllClick}>
                      <Trans>View all</Trans>
                    </HyperLink>
                  ) : (
                    <Link href={allBrandsLink} passHref legacyBehavior>
                      <HyperLink
                        hasUnderline
                        asLink
                        data-testid="view-all-brands"
                      >
                        <Trans>View all</Trans>
                      </HyperLink>
                    </Link>
                  )}
                </Box>
              ) : null}
            </Flex>
          </div>
          <Box mb={[32, 32, 32, 64, 64]} className={layout.bleedMobile}>
            <HomeBrandCover
              brand={featuredBrand}
              genderId={genderId}
              isComingSoon={!!featuredBrand.is_coming_soon}
              isGated={isGated}
              isMobile={isMobile}
              setRedirectData={setRedirectData}
            />
          </Box>
        </>
      )}

      <Box className={layout.containerGrid}>
        <ExploreMoreCarousel
          hideTitle={!featuredBrand}
          genderId={genderId}
          shopType={shopType}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          carouselData={carouselData}
        />
      </Box>
      {open && (
        <LoginRegisterModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          redirectTo={allBrandsLink}
          trackingData={{
            pageName: "homepage",
            shopType: genderId,
            actionSource,
            redirectTo: allBrandsLink,
          }}
          onRedirect={setRedirectData}
        />
      )}
    </Box>
  );
}

export default BrandsSection;
